import React, { useEffect, useState  } from 'react';
import { useNavigate } from 'react-router-dom';
import { Divider, Button, Spin, Switch, Drawer } from 'antd';
import constants from '../../../constants/constants';
import { getColorClass } from '../../../functions/functions';
import {CaretRightOutlined, PauseOutlined } from '@ant-design/icons';
import { fetchApi } from '../../../services/api';
import AudioComponent from './AudioComponent';
import { COOKIE, getCookie } from '../../../services/cookie';
import { handleKeyboardAction } from '../../../globalFunctions/GlobalFunctions';
import TranscriptSection from './TranscriptSection';

const AudioResources = () => {
	const navigate = useNavigate();
	const colorCode = constants.ColorCode;
	const [loader, setLoader] = useState(false);
	const [podcastList, setPodcastList] = useState([]);
	const [podcastDetails, setPodcastDetails] = useState(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const keyboardFocusableClass = constants.KeyboardFocusableClass;
	const [checked, setChecked] = useState(true);
	const [showTranscript, setShowTranscript] = useState(false);
	const [selectedTranscript, setSelectedTranscript] = useState('');
	const [podcastStatus, setPodcastStatus] = useState('');
	
	const routeToChapter = (chapter_id) => {
		const state = {
			chapter_id: chapter_id
		};
		navigate('/chapter-resources', { state });
	};
	const playPodcast = (details) => {
		if (podcastDetails?.chapter_id === details.chapter_id) {
		  const nextPlaying = !isPlaying;
		  setIsPlaying(nextPlaying);
		  setPodcastStatus(`${details.chapter_name} ${nextPlaying ? 'playing' : 'paused'}.`);
		} else {
		  setIsPlaying(false);
		  setPodcastDetails(details);
		  setPodcastStatus(`${details.chapter_name} loading.`);
		  setTimeout(() => {
			setIsPlaying(true);
			setPodcastStatus(`${details.chapter_name} playing.`);
		  }, 1000);
		}
	  };
	  
	const getPodcastList = () => {
		let userId = getCookie(COOKIE.UserId);
		setLoader(true);
		fetchApi(`/get-podcast-list-user`, 'post', { stud_id:userId }).then((res) => {
			if (res?.code === 200) {
				setPodcastList(res?.data)
				setLoader(false);
			} else {
				setPodcastList([]);
				setLoader(false);
			}
		})
	};
	useEffect(() => {
		getPodcastList();
	}, []);

	const getPreviewText = (text, sentenceCount = 3) => {
		const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
		if (sentences.length <= sentenceCount) {
			return {
				text,
				hasMore: false,
			};
		}
		return {
			text: sentences.slice(0, sentenceCount).join(' '),
			hasMore: true,
		};
	};
	const onChangeTranscript = (checked) => {
		setChecked(checked);
	};
	return (
		<div>
			<div className='container mx-auto pb-20'>
				<div className='flex justify-between items-center'>
					<h1 style={{ color: getColorClass("1", "colorcode") }} className='text-2xl underline underline-offset-4 font-extrabold uppercase'>{constants.Podcast + "s"}</h1>
					<Switch
						checkedChildren="Hide transcript"
						unCheckedChildren="Display transcript"
						className={!checked ? "bg-gray-500" : "!bg-[#0b3ea8]"}
						checked={checked}
						onChange={onChangeTranscript}
					/>
				</div>
				<Divider className='my-2' />
				<Spin spinning={loader} size="large" className='z-30'>
					{podcastList.length > 0 ? (
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
							{podcastList.map((val, index) => {
								const { text: previewText, hasMore } = getPreviewText(val?.transcript || '');
								return (
								<div
									key={val?.chapter_id}
									id={`podcast-${val.chapter_id}`}
									className="break-inside-avoid border flex flex-col gap-4 p-4 rounded-md"
									style={{ borderColor: colorCode[index % colorCode.length] }}
								>
									<div className="flex gap-4 ">
										{/* Chapter ID Block */}
										<div
											style={{ backgroundColor: colorCode[index % colorCode.length] }}
											className={`text-white text-2xl font-bold w-20 h-18 flex items-center justify-center rounded shrink-0 cursor-pointer ${keyboardFocusableClass}`}
											onClick={() => routeToChapter(val?.chapter_id)}
											onKeyDown={(event) => handleKeyboardAction(event, () => routeToChapter(val?.chapter_id))}
											role="button"
											tabIndex={0}
											aria-label={`Go to chapter ${val?.chapter_id}`}
										>
											{val?.chapter_id}
										</div>

										{/* Content */}
										<div className="flex flex-col justify-center w-full">
											{/* Title */}
											<div
												onClick={() => routeToChapter(val?.chapter_id)}
												onKeyDown={(event) => handleKeyboardAction(event, () => routeToChapter(val?.chapter_id))}
												className={`font-semibold text-base sm:text-lg text-gray-800 leading-tight cursor-pointer ${keyboardFocusableClass}`}
												role="button"
												tabIndex={0}
												aria-label={`Go to chapter ${val?.chapter_name}`}
											>
												{val?.chapter_name}
											</div>

											{/* Button + Played On */}
											<div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap mt-2">
												<Button
													type="primary"
													icon={
														podcastDetails?.chapter_id === val.chapter_id && isPlaying
															? <PauseOutlined />
															: <CaretRightOutlined />
													}
													className="text-white text-sm font-medium"
													style={{ backgroundColor: colorCode[index % colorCode.length] }}
													onClick={() => playPodcast(val)}
													aria-label={
														podcastDetails?.chapter_id === val.chapter_id && isPlaying
															? `Pause ${val.chapter_name}`
															: `Play ${val.chapter_name}`
													}
												>
													{podcastDetails?.chapter_id === val.chapter_id && isPlaying ? 'Pause' : 'Play'}
													{val.duration && <> • &nbsp;{val.duration}</>}
												</Button>

												{val?.played_on && (
													<span className="text-sm italic text-gray-500">
														Played on {val?.played_on}
													</span>
												)}
											</div>
										</div>
									</div>
										{
											checked && previewText && (
												<div
													className="p-3"
													style={{
														backgroundColor: colorCode[index % colorCode.length] + "15",
													}}
												>
													<p
														onCopy={(e) => e.preventDefault()}
														onCut={(e) => e.preventDefault()}
														onContextMenu={(e) => e.preventDefault()}
														onDragStart={(e) => e.preventDefault()}
														dangerouslySetInnerHTML={{
															__html: previewText,
														}}
													/>
													{hasMore && (
														<Button
															type='link'
															className={`${keyboardFocusableClass} text-[#0b3ea8] p-0`}
															onClick={() => {
																setSelectedTranscript(val?.transcript || '');
																setShowTranscript(true);
															}}
														>
															Transcript
														</Button>
													)}
												</div>
											)
										}
									</div>
								)
							})}
						</div>
					) : (
						<div className="text-gray-500">No podcasts found.</div>
					)}

					<div className="sr-only" aria-live="polite" aria-atomic="true">{podcastStatus}</div>
					<div className='fixed bottom-0 w-full left-0 shadow z-50'>
						{podcastDetails && (
							<AudioComponent
								chapter_id={podcastDetails.chapter_id}
								isPlayed={podcastDetails?.played_on ? true : false}
								chapterDetails={podcastDetails}
								isPlaying={isPlaying}
								setIsPlaying={setIsPlaying}
							/>
						)}
					</div>
					<Drawer
						title="Podcast Transcript"
						placement="right"
						width={600}
						open={showTranscript}
						onClose={() => {
							setShowTranscript(false);
						}}
						destroyOnClose
						maskClosable
					>
						<TranscriptSection
							transcript={selectedTranscript}
							title="In this podcast"
							ariaLabel="Podcast Transcript"
							className="lg:w-[450px] xl:w-[500px]"
						/>
					</Drawer>
				</Spin>
			</div>
		</div>
	)
}

export default AudioResources
